/* eslint-disable no-param-reassign */

import { createSlice } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import listSaga from './list';
import byIdSaga from './byId';
import childrenSaga from './children';
import createSaga, { PostCreateData } from './create';
import updateSaga, { PostUpdateData } from './update';
import deleteSaga from './delete';
import { BaseState, createCRUDModel, createCRUDStates } from '../creator';
import { RootState } from '../index';
import { FileData, PaginatedResponse } from '../types';
import { Article, ArticleStatus } from '../article';
import { User } from '../auth';
import { Business } from '../business';
import { CategoryShort } from '../category';
import { SubCategory } from '../subCategory';
import { Comment } from '../comment';

export const moduleName = 'post';

export type PostStatus = 'published' | 'deactivated' | 'draft';
export const PostStatusTitle: Record<PostStatus, string> = {
  published: 'Published',
  deactivated: 'Hidden',
  draft: 'Draft',
};

export type PostEntity = 'business' | 'article' | 'international_community' | 'category' | 'subcategory';
export const PostEntityTitle: Record<PostEntity, string> = {
  business: 'Business',
  article: 'Article',
  international_community: 'International community',
  category: 'Category',
  subcategory: 'Subcategory',
};

export interface PostFile {
  id: string;
  originalName: string;
  location: string;
  mimeType: string;
  createdAt: string;
}

export interface PostReaction {
  id: string;
  status: boolean;
}

export interface Post {
  id: string;
  text: string;
  status: PostStatus;
  entity: PostEntity;
  numberOfLikes: number;
  numberOfDislikes: number;
  numberOfComments: number;
  numberOfCommentsTotal: number;
  files: FileData[],
  user: User;
  business: Business | null,
  category: CategoryShort | null,
  subCategory: SubCategory | null,
  article: Article | null,
  createdAt: string;
  updatedAt: string;
  comments: Comment[];

  reactions?: PostReaction[];
}

export interface State {
  list: BaseState<PaginatedResponse<Post>>,
  byId: BaseState<Post>,
  childrenList: BaseState<PaginatedResponse<Post, { parentId: string }>>,
  create: BaseState<PostCreateData>,
  update: BaseState<PostUpdateData>,
  delete: BaseState<{ id: string }>
}

const models = ['list', 'childrenList', 'byId', 'create', 'update', 'delete'];

export const defaultState: State = createCRUDStates<State>(models);

export const post = createSlice({
  name: moduleName,
  initialState: defaultState,
  reducers: createCRUDModel(models, moduleName),
});

export const { actions } = post;

export function usePost(): State {
  return useSelector((state: RootState) => state[moduleName]);
}

export function* saga() {
  yield* listSaga();
  yield* byIdSaga();
  yield* childrenSaga();
  yield* createSaga();
  yield* updateSaga();
  yield* deleteSaga();
}
